import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { Mail, Lock, User, Fingerprint } from 'lucide-react';

const REMEMBER_ME_STORAGE_KEY = 'rememberedLogin';

const Login = () => {
  const [loginType, setLoginType] = useState('admin'); // admin, employee
  const [rememberMe, setRememberMe] = useState(false);
  const [savedCredentials, setSavedCredentials] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({ shouldUnregister: true });
  const tenantIdValue = watch('tenantId');
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      if (user?.role === 'system') {
        navigate('/system/tenants', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, navigate, from, user]);

  useEffect(() => {
    const rememberedForType = savedCredentials?.credentials?.[loginType];
    // Only reset to saved credentials if rememberMe is enabled and we have saved data for this type.
    if (rememberMe) {
      if (rememberedForType) {
        reset(rememberedForType);
      }
      // If user just checked "Remember me" but there are no saved credentials,
      // do not clear the current form values.
      return;
    }

    // When rememberMe is not enabled, clear the form.
    reset();
  }, [loginType, reset, rememberMe, savedCredentials]);

  useEffect(() => {
    const tenantId = String(tenantIdValue || '').trim().toLowerCase();
    if (tenantId.length < 2) {
      setSubscriptionExpired(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await authAPI.getTenantNotice(tenantId);
        if (!cancelled) setSubscriptionExpired(!!res.data?.subscriptionExpired);
      } catch {
        if (!cancelled) setSubscriptionExpired(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [tenantIdValue]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(REMEMBER_ME_STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (!parsed?.credentials) return;

      setSavedCredentials(parsed);
      setRememberMe(true);
      if (parsed.loginType === 'admin' || parsed.loginType === 'employee') {
        setLoginType(parsed.loginType);
      }
    } catch (error) {
      localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
    }
  }, []);

  const buildPayloadByLoginType = (data, type) => {
    if (type === 'admin') {
      return {
        tenantId: data.tenantId,
        email: data.email,
        password: data.password,
      };
    }
    if (type === 'employee') {
      return {
        tenantId: data.tenantId,
        employeeId: data.employeeId,
        password: data.password,
      };
    }
    return data;
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const payload = buildPayloadByLoginType(data, loginType);
      const result = await login(payload, loginType);
      if (result.success) {
        if (rememberMe) {
          const nextRemembered = {
            loginType,
            credentials: {
              ...(savedCredentials?.credentials || {}),
              [loginType]: payload,
            },
          };
          localStorage.setItem(REMEMBER_ME_STORAGE_KEY, JSON.stringify(nextRemembered));
          setSavedCredentials(nextRemembered);
        } else {
          localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
          setSavedCredentials(null);
        }
        if (result.user?.role === 'system') {
          navigate('/system/tenants', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (error) {

    } finally {
      setIsLoading(false);
    }
  };

  const renderRememberMe = () => (
    <label className="flex items-center gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        checked={rememberMe}
        onChange={(e) => {
          const checked = e.target.checked;
          setRememberMe(checked);
          if (!checked) {
            localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
            setSavedCredentials(null);
          }
        }}
        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      Remember me
    </label>
  );


  const renderLoginForm = () => {
    switch (loginType) {
      case 'admin':
        return (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label htmlFor="tenantId" className="block text-sm font-medium text-gray-700">
                Tenant
              </label>
              <input
                {...register('tenantId', { required: 'Tenant is required' })}
                type="text"
                className="input"
                placeholder="e.g. companyA"
              />
              {errors.tenantId && (
                <p className="mt-1 text-sm text-red-600">{errors.tenantId.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  type="email"
                  className="input pl-10"
                  placeholder="your.email@company.com"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password', { required: 'Password is required' })}
                  type="password"
                  className="input pl-10"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              {renderRememberMe()}
              <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                Forgot password?
              </Link>
            </div>
          </form>
        );

      case 'employee':
        return (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label htmlFor="tenantId" className="block text-sm font-medium text-gray-700">
                Tenant
              </label>
              <input
                {...register('tenantId', { required: 'Tenant is required' })}
                type="text"
                className="input"
                placeholder="e.g. companyA"
              />
              {errors.tenantId && (
                <p className="mt-1 text-sm text-red-600">{errors.tenantId.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="employeeId" className="block text-sm font-medium text-gray-700">
                Employee ID
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('employeeId', { required: 'Employee ID is required' })}
                  type="text"
                  className="input pl-10"
                  placeholder="Enter your Employee ID"
                />
              </div>
              {errors.employeeId && (
                <p className="mt-1 text-sm text-red-600">{errors.employeeId.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password', { required: 'Password is required' })}
                  type="password"
                  className="input pl-10"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              {renderRememberMe()}
              <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                Forgot password?
              </Link>
            </div>
          </form>
        );

      // biometric removed

      default:
        return null;
    }
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden flex items-center justify-center bg-[#fafafa] py-8 px-3 sm:py-12 sm:px-6 lg:px-8">
      <div className="absolute -top-24 -left-24 w-[22rem] h-[22rem] sm:w-[38rem] sm:h-[38rem] rounded-full bg-[radial-gradient(ellipse_at_center,_rgba(59,130,246,0.25),_transparent_60%)] blur-3xl animate-drift" />
      <div className="absolute -bottom-24 -right-24 w-[22rem] h-[22rem] sm:w-[38rem] sm:h-[38rem] rounded-full bg-[radial-gradient(ellipse_at_center,_rgba(16,185,129,0.25),_transparent_60%)] blur-3xl animate-drift-2" />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <Fingerprint className="w-[18rem] h-[18rem] sm:w-[46rem] sm:h-[46rem] text-primary-200 opacity-10 animate-pulse-glow" />
      </div>
      <div className="relative max-w-4xl w-full min-w-0 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <div className="hidden md:flex items-center justify-center">
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="absolute w-64 h-64 rounded-full border border-primary-200 animate-rotate-slow" />
            <div className="absolute w-80 h-80 rounded-full border border-primary-100 animate-rotate-slow" style={{ animationDuration: '36s' }} />
            <div className="relative w-48 h-48 bg-primary-100 rounded-full flex items-center justify-center shadow-lg">
              <Fingerprint className="w-20 h-20 text-primary-600" />
            </div>
          </div>
        </div>
        <div className="w-full min-w-0 space-y-6 sm:space-y-8">
          <div>
            <div className="mx-auto h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center">
              <Fingerprint className="h-8 w-8 text-white" />
            </div>
            <h2 className="mt-6 text-center md:text-left text-2xl sm:text-3xl font-semibold text-gray-900">
              Sign in to eAMS
            </h2>
            <p className="mt-2 text-center md:text-left text-sm text-gray-600">
              Employee Attendance Management System
            </p>
          </div>

          <div className="bg-white border border-gray-200 shadow-sm rounded-lg py-6 px-4 sm:py-8 sm:px-10 animate-fade-up">
            {subscriptionExpired && (
              <div className="mb-5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs sm:text-sm text-amber-900">
                This subscription has expired. Tenant admins can still sign in. Please contact your administrator.
              </div>
            )}
            <div className="mb-6">
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                <button
                  type="button"
                  onClick={() => setLoginType('admin')}
                  className={`flex-1 py-2.5 px-3 rounded-md text-sm font-medium transition-colors ${loginType === 'admin'
                      ? 'bg-white text-primary-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Admin
                </button>
                <button
                  type="button"
                  onClick={() => setLoginType('employee')}
                  className={`flex-1 py-2.5 px-3 rounded-md text-sm font-medium transition-colors ${loginType === 'employee'
                      ? 'bg-white text-primary-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Employee
                </button>
                {/* Biometric tab removed */}
              </div>
            </div>
            {renderLoginForm()}

            <div className="pt-4 border-t border-gray-100 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3 text-sm">
              <Link to="/system-login" className="font-medium text-primary-600 hover:text-primary-700">
                System login
              </Link>
              <span className="text-xs sm:text-sm text-gray-500">Separate access for system users</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
